import React, { createContext, useEffect, useState, ReactNode } from 'react';
import webSocketService from './webSocketService';
import { useToken } from '../common/helper';
import { useSelector } from 'react-redux';
import { GetWsTicket } from '../apis/auth';
import { getSessionSignal } from '@/common/interceptor/sessionAbortController';

interface WebSocketContextValue {
  webSocketService: typeof webSocketService;
  selectedUserID?: string | null;
  setSelectedUserID: (id?: string | null) => void;
}

interface WebSocketProviderProps {
  children: ReactNode;
}

interface RootState {
  auth: {
    isAuthenticated: boolean;
  };
}

/**
 * WebSocket context for managing real-time connections across the application.
 * Handles authentication-based connection establishment and user selection.
 */
const WebSocketContext = createContext<WebSocketContextValue | undefined>(
  undefined
);

/**
 * Provider component that manages WebSocket connections based on authentication state.
 * Automatically connects/disconnects based on token and user ID availability.
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const { token, getUserID, setUserId, getToken, setToken, getPreviewState } =
    useToken();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const [selectedUserID, setSelectedUserID] = useState<string | null>(null);
  const activeUserID = selectedUserID || getUserID();
  const isPreview = getPreviewState()?.isPreview;

  useEffect(() => {
    if (!token || !activeUserID) {
      setToken(getToken());
      setUserId(getUserID());
      return;
    }

    if (isPreview) {
      webSocketService.disconnect();
      return;
    }

    const baseDomain = process.env.REACT_APP_WEB_SOCKETS;
    const signal = getSessionSignal();
    let cancelled = false;

    const onAbort = () => {
      cancelled = true;
      webSocketService.disconnect();
    };

    signal.addEventListener('abort', onAbort, { once: true });

    const connect = async () => {
      try {
        const res = await GetWsTicket();
        const ticket = res?.data?.ticket;
        if (cancelled || signal.aborted) return;
        if (ticket) {
          webSocketService?.connect(
            `${baseDomain}/chat/${activeUserID}/?ticket=${ticket}`
          );
        }
      } catch (error: unknown) {
        const err = error as { code?: string; name?: string; message?: string };
        if (
          cancelled ||
          signal.aborted ||
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          err?.message?.toLowerCase().includes('canceled') ||
          err?.message?.toLowerCase().includes('cancelled')
        ) {
          return;
        }
        // fallback — will be removed once backend confirms ticket endpoint is stable
        webSocketService?.connect(
          `${baseDomain}/chat/${activeUserID}/?token=${token}`
        );
      }
    };

    connect();

    return () => {
      cancelled = true;
      signal.removeEventListener('abort', onAbort);
      webSocketService.disconnect();
    };
  }, [
    token,
    activeUserID,
    isAuthenticated,
    setToken,
    getToken,
    setUserId,
    getUserID,
    isPreview,
  ]);

  return (
    <WebSocketContext.Provider
      value={{ webSocketService, selectedUserID, setSelectedUserID }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to access the WebSocket context.
 * Must be used within a WebSocketProvider.
 */
export const useWebSocket = (): WebSocketContextValue | undefined =>
  React.useContext(WebSocketContext);
